// src/components/pricing/PricingPage.jsx
//
// Public /pricing page. Everything on it comes from GET /api/billing/plans:
// plan rows (price, trial, included pools, fees) merged server-side with
// server/config/plans.js (limits, tagline, highlights, FAQ, nonprofit
// discount). Nothing about a plan is hard-coded here.
//
// Shares the .osep marketing-page styles (header, hero, sections, FAQ,
// footer) with the SEO event pages; PricingPage.css adds the cards, the
// comparison table and the nonprofit band.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getJson, postJson } from '../../services/http';
import { SITE, SIGNUP_URL, DOCS_URL } from '../../seo/eventPages';
import '../seo/EventSoftwarePage.css';
import './PricingPage.css';

const LOGO = `${process.env.PUBLIC_URL}/on-screen-logo-white-bezel-h160.png`;
const TITLE = 'Pricing | On-Screen — One Price per Organization';
const DESCRIPTION = 'On-Screen plans for nonprofits, churches, schools and event organizers: one price for your organization, every module included, and a nonprofit discount.';

function money(n, { cents = true } = {}) {
  const v = Number(n) || 0;
  return `$${v.toFixed(cents || v % 1 !== 0 ? 2 : 0)}`;
}

function fill(text, vars) {
  return String(text || '').replace(/\{\{(\w+)\}\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
}

// Title, description and canonical for /pricing; restores the previous tags
// on unmount (same approach as EventSoftwarePage's useSeo, without JSON-LD).
function usePricingHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const restore = [];
    const setTag = (selector, create, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) { el = create(); document.head.appendChild(el); }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      restore.push(() => (prev == null ? el.remove() : el.setAttribute(attr, prev)));
    };
    const meta = (attr, key) => () => { const m = document.createElement('meta'); m.setAttribute(attr, key); return m; };
    setTag('meta[name="description"]', meta('name', 'description'), 'content', DESCRIPTION);
    setTag('meta[property="og:title"]', meta('property', 'og:title'), 'content', TITLE);
    setTag('meta[property="og:description"]', meta('property', 'og:description'), 'content', DESCRIPTION);
    setTag('meta[property="og:url"]', meta('property', 'og:url'), 'content', `${SITE}/pricing`);
    setTag('link[rel="canonical"]', () => { const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l; }, 'href', `${SITE}/pricing`);
    return () => {
      document.title = prevTitle;
      restore.reverse().forEach((fn) => fn());
    };
  }, []);
}

/** One line per public limit, e.g. "5 team members included". */
export function limitLines(plan, limitKeys, t) {
  const L = plan.limits || {};
  const lines = [];
  const has = (k) => limitKeys.includes(k);
  const num = (v) => Number(v).toLocaleString();
  if (has('users')) {
    if (L.users === null) lines.push(t('pricing.limit.usersUnlimited', 'Unlimited team members'));
    else {
      lines.push(t('pricing.limit.users', { defaultValue: '{{count}} team members included', count: num(L.users) }));
      if (Number(plan.extra_user_price) > 0) {
        lines.push(t('pricing.limit.extraUsers', { defaultValue: 'Extra team members {{price}}/mo each', price: money(plan.extra_user_price) }));
      }
    }
  }
  if (has('posts_per_month')) {
    lines.push(L.posts_per_month === null
      ? t('pricing.limit.postsUnlimited', 'Unlimited posts')
      : t('pricing.limit.posts', { defaultValue: '{{count}} posts a month', count: num(L.posts_per_month) }));
  }
  if (has('scheduled_posts_per_month') && L.posts_per_month !== null) {
    lines.push(L.scheduled_posts_per_month === null
      ? t('pricing.limit.scheduledUnlimited', 'Unlimited scheduled posts')
      : t('pricing.limit.scheduled', { defaultValue: '{{count}} scheduled posts a month', count: num(L.scheduled_posts_per_month) }));
  }
  if (has('events_per_month')) {
    lines.push(L.events_per_month === null
      ? t('pricing.limit.eventsUnlimited', 'Unlimited events')
      : t('pricing.limit.events', { defaultValue: '{{count}} new events a month', count: num(L.events_per_month) }));
  }
  if (has('tickets_per_month')) {
    if (L.tickets_per_month === null) lines.push(t('pricing.limit.ticketsUnlimited', 'Paid tickets with no On-Screen fee'));
    else lines.push(t('pricing.limit.tickets', {
      defaultValue: '{{count}} paid tickets a month included, then {{fee}} each',
      count: num(L.tickets_per_month), fee: money(plan.overage_ticket_fee),
    }));
  }
  if (has('raffle_entries_per_month')) {
    if (L.raffle_entries_per_month === null) lines.push(t('pricing.limit.raffleUnlimited', 'Paid raffle entries with no On-Screen fee'));
    else lines.push(t('pricing.limit.raffle', {
      defaultValue: '{{count}} paid raffle entries a month included, then {{fee}} each',
      count: num(L.raffle_entries_per_month), fee: money(plan.overage_raffle_entry_fee),
    }));
  }
  if (has('analytics_history_days')) {
    lines.push(L.analytics_history_days === null
      ? t('pricing.limit.historyUnlimited', 'Analytics history kept for as long as you’re a customer')
      : t('pricing.limit.history', { defaultValue: '{{count}} days of analytics history', count: num(L.analytics_history_days) }));
  }
  if (has('saved_reports')) {
    lines.push(L.saved_reports === null
      ? t('pricing.limit.reportsUnlimited', 'Unlimited saved reports')
      : t('pricing.limit.reports', { defaultValue: '{{count}} saved custom reports', count: num(L.saved_reports) }));
  }
  if (has('ai_captions_per_month')) {
    lines.push(L.ai_captions_per_month === null
      ? t('pricing.limit.aiUnlimited', 'Unlimited AI caption help')
      : Number(L.ai_captions_per_month) === 0
        ? t('pricing.limit.aiNone', 'No AI caption help')
        : t('pricing.limit.ai', { defaultValue: '{{count}} AI caption suggestions a month', count: num(L.ai_captions_per_month) }));
  }
  return lines;
}

function PlanCard({ plan, limitKeys, t }) {
  return (
    <article className={`ospr-card${plan.featured ? ' ospr-card--featured' : ''}`} aria-labelledby={`ospr-plan-${plan.id}`}>
      {plan.featured && <p className="ospr-badge">{t('pricing.mostPopular', 'Most popular')}</p>}
      <h3 id={`ospr-plan-${plan.id}`}>{plan.name}</h3>
      {plan.tagline && <p className="ospr-tagline">{plan.tagline}</p>}
      <p className="ospr-price">
        <span className="ospr-amount">{money(plan.price_monthly, { cents: false })}</span>
        <span className="ospr-period">{t('pricing.perOrgMonth', '/month per organization')}</span>
      </p>
      {Number(plan.trial_days) > 0 && (
        <p className="ospr-trial">{t('pricing.trial', { defaultValue: '{{days}}-day free trial', days: plan.trial_days })}</p>
      )}
      <ul className="ospr-list">
        {limitLines(plan, limitKeys, t).map((line) => <li key={line}>{line}</li>)}
      </ul>
      {plan.highlights && plan.highlights.length > 0 && (
        <ul className="ospr-list ospr-list--highlights">
          {plan.highlights.map((h) => <li key={h}>{h}</li>)}
        </ul>
      )}
      <a className="osep-btn osep-btn--primary ospr-cta" href={SIGNUP_URL}>
        {t('pricing.startTrial', 'Start free trial')}
      </a>
    </article>
  );
}

export default function PricingPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  usePricingHead();

  useEffect(() => {
    document.body.classList.add('osep-page-active');
    window.scrollTo(0, 0);
    postJson('/api/tracking/site-visit', { path: '/pricing' }).catch(() => {});
    return () => document.body.classList.remove('osep-page-active');
  }, []);

  useEffect(() => {
    let cancelled = false;
    getJson('/api/billing/plans')
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  const plans = useMemo(() => (data && data.plans) || [], [data]);
  const limitKeys = (data && data.limitKeys) || [];
  const nonprofit = data && data.nonprofit;
  const faqVars = useMemo(() => {
    const extra = plans.map((p) => Number(p.extra_user_price)).filter((n) => n > 0);
    return {
      extraUserPrice: extra.length ? money(Math.min(...extra)) : '$1.00',
      percentOff: nonprofit ? nonprofit.percentOff : '',
    };
  }, [plans, nonprofit]);
  const faq = ((data && data.faq) || []).filter((f) => f.id !== 'nonprofit' || (nonprofit && nonprofit.enabled));

  return (
    <div className="osep ospr">
      <header className="osep-top">
        <Link to="/about" className="osep-logo" aria-label="On-Screen home">
          <img src={LOGO} alt="On-Screen" height="40" />
        </Link>
        <nav aria-label={t('pricing.navLabel', 'Site')} className="osep-nav">
          <Link to="/about">{t('pricing.navFeatures', 'Features')}</Link>
          <Link to="/pricing" className="is-active" aria-current="page">{t('pricing.navPricing', 'Pricing')}</Link>
          <a href={DOCS_URL}>{t('pricing.navDocs', 'User guide')}</a>
          <Link to="/login">{t('pricing.navSignIn', 'Sign in')}</Link>
        </nav>
      </header>

      <main>
        <section className="osep-hero">
          <p className="osep-eyebrow">{t('pricing.eyebrow', 'Pricing')}</p>
          <h1>{t('pricing.h1', 'One price for your whole organization')}</h1>
          <p className="osep-intro">
            {t('pricing.intro', 'Not per user, not per module. Every plan includes social scheduling, events, ticketing and check-in, raffles, auctions, forms and reports. Plans differ in how much you can do each month.')}
          </p>
          <div className="osep-ctas">
            <a className="osep-btn osep-btn--primary" href={SIGNUP_URL}>{t('pricing.startTrial', 'Start free trial')}</a>
            <a className="osep-btn osep-btn--ghost" href="#ospr-compare">{t('pricing.compareCta', 'Compare plans')}</a>
          </div>
        </section>

        {nonprofit && nonprofit.enabled && (
          <section className="ospr-nonprofit" aria-labelledby="ospr-np-title">
            <div>
              <h2 id="ospr-np-title">
                {t('pricing.nonprofitTitle', { defaultValue: 'Nonprofit? Get {{percent}}% off any plan.', percent: nonprofit.percentOff })}
              </h2>
              <p>{nonprofit.eligibility}</p>
              <p>{t('pricing.nonprofitHow', 'Sign up, then apply from Billing with your EIN or registration number. We review applications within a few business days.')}</p>
            </div>
            <a className="osep-btn osep-btn--primary" href="/docs/OnScreen_45_Nonprofit_Discount.html">
              {t('pricing.nonprofitCta', 'How to apply')}
            </a>
          </section>
        )}

        <section className="osep-section" aria-labelledby="ospr-plans-title">
          <h2 id="ospr-plans-title">{t('pricing.plansTitle', 'Plans')}</h2>
          {!data && !error && <p className="ospr-status">{t('pricing.loading', 'Loading plans…')}</p>}
          {error && <p className="ospr-status" role="alert">{t('pricing.unavailable', 'Pricing is temporarily unavailable. Please check back soon.')}</p>}
          {plans.length > 0 && (
            <div className="ospr-grid">
              {plans.map((p) => <PlanCard key={p.id} plan={p} limitKeys={limitKeys} t={t} />)}
            </div>
          )}
          <p className="ospr-footnote">
            {t('pricing.footnote', 'Prices in US dollars. Members, attendees, donors and ticket buyers never count as team members. Card processing fees from Stripe or Square are separate.')}
          </p>
        </section>

        {plans.length > 0 && (
          <section className="osep-section osep-section--tint" id="ospr-compare" aria-labelledby="ospr-compare-title">
            <h2 id="ospr-compare-title">{t('pricing.compareTitle', 'Compare plans')}</h2>
            <div className="ospr-table-wrap" tabIndex={0} role="region" aria-labelledby="ospr-compare-title">
              <table className="ospr-table">
                <thead>
                  <tr>
                    <th scope="col">{t('pricing.compareIncluded', 'Included each month')}</th>
                    {plans.map((p) => <th scope="col" key={p.id}>{p.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">{t('pricing.comparePrice', 'Price')}</th>
                    {plans.map((p) => <td key={p.id}>{money(p.price_monthly, { cents: false })}{t('pricing.perMonthShort', '/mo')}</td>)}
                  </tr>
                  {limitKeys.map((k) => (
                    <tr key={k}>
                      <th scope="row">{t(`pricing.limitName.${k}`, k)}</th>
                      {plans.map((p) => {
                        const v = p.limits ? p.limits[k] : undefined;
                        return <td key={p.id}>{v === null ? t('pricing.unlimited', 'Unlimited') : v === undefined ? '—' : Number(v).toLocaleString()}</td>;
                      })}
                    </tr>
                  ))}
                  <tr>
                    <th scope="row">{t('pricing.compareTicketFee', 'Fee per paid ticket past the pool')}</th>
                    {plans.map((p) => <td key={p.id}>{Number(p.is_unlimited_tickets) ? t('pricing.none', 'None') : money(p.overage_ticket_fee)}</td>)}
                  </tr>
                  <tr>
                    <th scope="row">{t('pricing.compareExtraUser', 'Each extra team member')}</th>
                    {plans.map((p) => <td key={p.id}>{Number(p.is_unlimited_users) ? t('pricing.none', 'None') : `${money(p.extra_user_price)}${t('pricing.perMonthShort', '/mo')}`}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {faq.length > 0 && (
          <section className="osep-section" aria-labelledby="ospr-faq-title">
            <h2 id="ospr-faq-title">{t('pricing.faqTitle', 'Pricing questions')}</h2>
            <div className="osep-faq">
              {faq.map((f) => (
                <details key={f.id}>
                  <summary>{f.q}</summary>
                  <p>{fill(f.a, faqVars)}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <section className="osep-final">
          <h2>{t('pricing.finalTitle', 'Try every module free')}</h2>
          <p>{t('pricing.finalBody', 'Start a free trial, set up your first event, and pick a plan when you’re ready.')}</p>
          <a className="osep-btn osep-btn--primary" href={SIGNUP_URL}>{t('pricing.startTrial', 'Start free trial')}</a>
        </section>
      </main>

      <footer className="osep-footer">
        <a href="/about">{t('pricing.footerAbout', 'About On-Screen')}</a>
        <a href={DOCS_URL}>{t('pricing.footerDocs', 'Documentation')}</a>
        <a href="/termsofuse">{t('pricing.footerTerms', 'Terms')}</a>
        <a href="/privacypolicy">{t('pricing.footerPrivacy', 'Privacy')}</a>
        <span>© SE3D Inc.</span>
      </footer>
    </div>
  );
}
